import React, { useState } from 'react';
import {
  FiX as X,
  FiDatabase as Database,
  FiCloud as Cloud,
  FiGlobe as Globe,
  FiRefreshCw as RefreshCw,
  FiAlertTriangle as AlertTriangle,
  FiCheckCircle as CheckCircle
} from 'react-icons/fi';
import { createVectorExport } from '../../../../../services/vectorExportsApi';

interface AwsRegion {
  value: string;
  label: string;
}

const AWS_REGIONS: AwsRegion[] = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-west-2', label: 'Europe (London)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' }
];

export interface Dataset {
  id: string;
  name: string;
  description?: string;
  test_case_count?: number;
  item_count?: number;
}

export interface AwsAccount {
  id: string;
  account_id: string;
  role_name: string;
  account_type?: string;
}

interface FormData {
  dataset_id: string;
  aws_account_id: string;
  display_name: string;
  description: string;
  region: string;
  auto_sync: boolean;
  embedding_source: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  datasets: Dataset[];
  awsAccounts: AwsAccount[];
}

const CreateVectorExportModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, datasets, awsAccounts }) => {
  const [formData, setFormData] = useState<FormData>({
    dataset_id: '',
    aws_account_id: '',
    display_name: '',
    description: '',
    region: 'us-east-1',
    auto_sync: false,
    embedding_source: 'aivilo'
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof FormData, value: string | boolean): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate display name when dataset is selected
    if (field === 'dataset_id' && value && !formData.display_name) {
      const dataset = datasets.find(d => d.id === value);
      if (dataset) {
        setFormData(prev => ({
          ...prev,
          display_name: `${dataset.name} Vectors`
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    try {
      await createVectorExport(formData);
      onSuccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create vector export';
      console.error('Error creating export:', err);
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const selectedDataset = datasets.find(d => d.id === formData.dataset_id);
  const selectedAccount = awsAccounts.find(a => a.id === formData.aws_account_id);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Export Dataset to S3 Vectors
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <p className="text-red-800 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          {/* Dataset Selection */}
          <div>
            <label htmlFor="source-dataset" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Database className="w-4 h-4 inline mr-2" />
              Source Dataset *
            </label>
            <select id="source-dataset"
              required
              value={formData.dataset_id}
              onChange={(e) => handleChange('dataset_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a dataset...</option>
              {datasets.map(dataset => (
                <option key={dataset.id} value={dataset.id}>
                  {dataset.name} ({dataset.test_case_count ?? dataset.item_count ?? 0} items)
                </option>
              ))}
            </select>
            {selectedDataset && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {selectedDataset.description ?? 'No description'}
              </p>
            )}
          </div>

          {/* AWS Account Selection */}
          <div>
            <label htmlFor="destination-aws-account" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Cloud className="w-4 h-4 inline mr-2" />
              Destination AWS Account *
            </label>
            <select id="destination-aws-account"
              required
              value={formData.aws_account_id}
              onChange={(e) => handleChange('aws_account_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select an AWS account...</option>
              {awsAccounts.filter(acc => acc.account_type !== 'payer' && acc.account_type !== 'partner').map(account => (
                <option key={account.id} value={account.id}>
                  Account {account.account_id} ({account.role_name})
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Vectors will be stored in this AWS account. You retain full ownership of the data.
            </p>
          </div>

          {/* Region Selection */}
          <div>
            <label htmlFor="aws-region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Globe className="w-4 h-4 inline mr-2" />
              AWS Region
            </label>
            <select id="aws-region"
              value={formData.region}
              onChange={(e) => handleChange('region', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              {AWS_REGIONS.map(region => (
                <option key={region.value} value={region.value}>
                  {region.label} ({region.value})
                </option>
              ))}
            </select>
          </div>

          {/* Display Name */}
          <div>
            <label htmlFor="display-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Display Name *
            </label>
            <input id="display-name"
              type="text"
              required
              value={formData.display_name}
              onChange={(e) => handleChange('display_name', e.target.value)}
              placeholder="e.g., FAQ Vectors for RAG"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
              placeholder="Optional description for this export"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Auto-Sync Toggle */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer" aria-label="Auto-Sync">
              <input
                type="checkbox"
                checked={formData.auto_sync}
                onChange={(e) => handleChange('auto_sync', e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                  <RefreshCw className="w-4 h-4" />
                  Auto-Sync
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Automatically sync vectors when the source dataset changes.
                  New items will be added, changed items updated, and deleted items removed.
                </div>
              </div>
            </label>
          </div>

          {/* Summary Card */}
          {formData.dataset_id && formData.aws_account_id && (
            <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Export Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Source Dataset:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{selectedDataset?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Items to Export:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedDataset?.test_case_count ?? selectedDataset?.item_count ?? 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Destination:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    AWS {selectedAccount?.account_id} / {formData.region}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Embedding Model:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    Amazon Titan V2 (1024 dim)
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* IAM Permissions Note */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800 dark:text-yellow-200">IAM Permissions Required</h4>
                <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                  Make sure your IAM role includes S3 Vectors permissions (s3vectors:*).
                  Check the updated policy in Hub &gt; AWS Environments.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !formData.dataset_id || !formData.aws_account_id || !formData.display_name}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Create Export
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateVectorExportModal;
